import type { ReactNode } from 'react'
import { tokens } from '@/theme'

interface Props {
  /** Small uppercase label above the title — typically the section name. */
  eyebrow: string
  /** Page title. */
  title: string
  /** Optional supporting line under the title. */
  lede?: string
  /** Right-aligned slot — search box, filter chips, "create" button, etc. */
  actions?: ReactNode
}

/**
 * Full-width page header used on tab landing pages where a Hero would be
 * too heavy. Eyebrow + title on the left, optional actions on the right.
 *
 * Styling mirrors the reference SPA's "section opener" — gold eyebrow,
 * 32 px headline, a thin gold rule on the left.
 */
export function PageHeader({ eyebrow, title, lede, actions }: Props) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
        marginBottom: 32,
        paddingBottom: 20,
        borderBottom: `1px solid ${tokens.line}`,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 12,
          }}
        >
          <div style={{ width: 32, height: 2, background: tokens.gold }} />
          <span
            style={{
              fontSize: 11,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: tokens.gold,
              fontFamily: tokens.fontDisplay,
            }}
          >
            {eyebrow}
          </span>
        </div>
        <h1
          style={{
            fontSize: tokens.sizeH1,
            fontWeight: tokens.weightDemi,
            color: tokens.ink,
            lineHeight: 1.15,
            margin: 0,
          }}
        >
          {title}
        </h1>
        {lede && (
          <p
            style={{
              marginTop: 10,
              fontSize: tokens.sizeBody,
              color: tokens.ink3,
              maxWidth: 640,
              lineHeight: 1.55,
            }}
          >
            {lede}
          </p>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>}
    </header>
  )
}
